import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Clock, XCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PayComplete() {
  const { tenantSlug } = useParams();
  const [params] = useSearchParams();
  const link = params.get("link");
  const [d, setD] = useState(null);
  const [left, setLeft] = useState(8);
  const navigate = useNavigate();

  useEffect(() => {
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/ticketing/payment-links/${link}`, { headers: { "X-Tenant-Slug": tenantSlug } });
        setD(r.data);
        if (r.data.status === "pending" && !params.get("cancelled") && ++tries < 10) timer = setTimeout(poll, 3000);
      } catch { setD({ status: "error" }); }
    };
    poll();
    return () => clearTimeout(timer);
  }, [link]); // eslint-disable-line react-hooks/exhaustive-deps

  const paid = d?.status === "paid";
  useEffect(() => {
    if (!paid) return;
    if (left <= 0) { navigate(`/${tenantSlug}`); return; }
    const t = setTimeout(() => setLeft(left - 1), 1000);
    return () => clearTimeout(t);
  }, [paid, left]); // eslint-disable-line react-hooks/exhaustive-deps
  const pending = d?.status === "pending";
  const Icon = !d ? Loader2 : paid ? CheckCircle2 : pending ? Clock : XCircle;
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md w-full text-center" data-testid="pay-complete">
        <Icon className={`w-14 h-14 mx-auto mb-3 ${!d ? "animate-spin text-slate-400" : paid ? "text-green-600" : pending ? "text-amber-500" : "text-red-500"}`} />
        <h1 className="text-2xl font-extrabold text-[#1e3a5f] mb-2" data-testid="pay-complete-title">
          {!d ? "Checking payment…" : paid ? "Payment received" : pending ? "Payment not completed yet" : d.status === "error" ? "Link not found" : "This link has expired"}
        </h1>
        {d?.event_name && <p className="text-slate-600 mb-1">{d.lines || `${d.quantity}× ${d.tier_name}${d.variant_name ? ` – ${d.variant_name}` : ""}`} · {d.event_name}</p>}
        {paid && (
          <div className="rounded-xl bg-green-50 text-green-800 p-3 mt-3 text-sm" data-testid="pay-complete-paid">
            Thank you! Your booking is confirmed and your tickets are on their way to your email.
            <p className="text-xs text-green-700 mt-1" data-testid="pay-complete-redirect">Taking you back to the shop in {left}s…</p>
          </div>
        )}
        {pending && d.checkout_url && (
          <a href={d.checkout_url} className="inline-block mt-4 px-6 py-3 rounded-full bg-[#1e3a5f] text-white font-bold" data-testid="pay-complete-retry">Pay €{d.total.toFixed(2)}</a>
        )}
        {d && !paid && !pending && d.status !== "error" && <p className="text-sm text-slate-500">The seat reservation was released. Please contact the organiser for a new link.</p>}
        {d && (
          <button onClick={() => navigate(`/${tenantSlug}`)} className="mt-5 w-full py-3 rounded-full border border-slate-200 text-[#1e3a5f] font-semibold hover:bg-slate-50 transition-colors" data-testid="pay-complete-shop-btn">Back to shop</button>
        )}
      </div>
    </div>
  );
}
